
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useUser } from "@/hooks/use-user";
import { useCommissionRequests } from "@/hooks/commission/use-commission-requests";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft,
  Info,
  Clock,
  FileText,
  Wallet,
  Search,
  ArrowUpDown
} from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { CommissionRequest } from "@/types/commission.types";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500",
  accepted: "bg-blue-500",
  in_progress: "bg-purple-500", 
  revision: "bg-orange-500",
  completed: "bg-green-500",
  rejected: "bg-red-500"
};

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  accepted: "Aceita",
  in_progress: "Em Progresso", 
  revision: "Em Revisão",
  completed: "Concluída",
  rejected: "Rejeitada"
};

export default function MyCommissionRequestsPage() {
  const navigate = useNavigate();
  const { user, isLoading: isUserLoading } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc'>('date-desc');

  const {
    data: requests,
    isLoading: isRequestsLoading,
    error
  } = useCommissionRequests(user?.id || "");

  if (isUserLoading) {
    return (
      <DashboardLayout title="Minhas Solicitações">
        <div className="container mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p>Carregando...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  // Filter and sort requests
  const filteredRequests = (requests || []).filter((request) => {
    const matchesSearch = !searchQuery || 
      request.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.commission_offer_id?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = !statusFilter || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortBy === 'date-desc' ? dateB - dateA : dateA - dateB;
  });

  // Group requests by role
  const myRequestsAsClient = sortedRequests.filter(request => request.client_id === user.id);
  const myRequestsAsArtist = sortedRequests.filter(request => request.artist_id === user.id);

  return (
    <DashboardLayout title="Minhas Solicitações de Comissão">
      <div className="container mx-auto pb-12">
        <div className="mb-6">
          <Button variant="outline" size="sm" asChild>
            <Link to="/commissions" className="flex items-center gap-1">
              <ChevronLeft className="h-4 w-4" />
              Voltar para Comissões
            </Link>
          </Button>
        </div>

        <div className="mb-8 flex flex-col-reverse md:flex-row justify-between md:items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar solicitações..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter || ""}
              onValueChange={(value) => setStatusFilter(value || null)}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos os Status</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="accepted">Aceita</SelectItem>
                <SelectItem value="in_progress">Em Progresso</SelectItem>
                <SelectItem value="revision">Em Revisão</SelectItem>
                <SelectItem value="completed">Concluída</SelectItem>
                <SelectItem value="rejected">Rejeitada</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={sortBy}
              onValueChange={(value: 'date-desc' | 'date-asc') => setSortBy(value)}
            >
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-desc">Mais recentes primeiro</SelectItem>
                <SelectItem value="date-asc">Mais antigas primeiro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            onClick={() => navigate("/contracts/nft-info")}
            className="flex items-center gap-2"
          >
            <Info className="h-4 w-4" />
            Informações do Contrato
          </Button>
        </div>

        <Tabs defaultValue="client">
          <TabsList className="mb-6">
            <TabsTrigger value="client">Solicitadas por Mim</TabsTrigger>
            <TabsTrigger value="artist">Recebidas (como Artista)</TabsTrigger>
          </TabsList>

          <TabsContent value="client">
            {isRequestsLoading ? (
              <div className="flex justify-center py-12">
                <p>Carregando suas solicitações...</p>
              </div>
            ) : myRequestsAsClient.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Minhas Solicitações</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Prazo</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Pagamento</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myRequestsAsClient.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell className="font-medium">
                            #{request.id.substring(0, 8)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(request.created_at), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${statusColors[request.status]} text-white`}
                            >
                              {statusLabels[request.status]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {request.delivery_deadline ? (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                <span>
                                  {format(new Date(request.delivery_deadline), 'dd/MM/yyyy')}
                                </span>
                              </div>
                            ) : (
                              "Não definido"
                            )}
                          </TableCell>
                          <TableCell>
                            {request.payment_method === 'wallet' ? (
                              <span className="text-amber-500 font-medium">
                                Ξ{request.final_price_eth}
                              </span>
                            ) : (
                              <span className="font-medium">
                                ${request.final_price_usd}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={request.payment_method === 'wallet' ? "outline" : "secondary"}>
                              {request.payment_method === 'wallet' ? (
                                <div className="flex items-center gap-1">
                                  <Wallet className="h-3 w-3" />
                                  <span>NFT</span>
                                </div>
                              ) : request.payment_method === 'pix' ? (
                                'PIX'
                              ) : (
                                'Cartão'
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/commissions/requests/${request.id}`)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12 border rounded-md bg-muted/20">
                <h3 className="text-lg font-medium mb-2">Nenhuma solicitação encontrada</h3>
                <p className="text-muted-foreground mb-4">
                  Você ainda não solicitou nenhuma comissão ou todas foram filtradas.
                </p>
                <Button onClick={() => navigate('/commissions')}>
                  Explorar Comissões
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="artist">
            {isRequestsLoading ? (
              <div className="flex justify-center py-12">
                <p>Carregando solicitações recebidas...</p>
              </div>
            ) : myRequestsAsArtist.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Solicitações Recebidas</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Prazo</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Pagamento</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myRequestsAsArtist.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell className="font-medium">
                            #{request.id.substring(0, 8)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(request.created_at), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${statusColors[request.status]} text-white`}
                            >
                              {statusLabels[request.status]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {request.delivery_deadline ? (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                <span>
                                  {format(new Date(request.delivery_deadline), 'dd/MM/yyyy')}
                                </span>
                              </div>
                            ) : (
                              "Não definido"
                            )}
                          </TableCell>
                          <TableCell>
                            {request.payment_method === 'wallet' ? (
                              <span className="text-amber-500 font-medium">
                                Ξ{request.final_price_eth}
                              </span>
                            ) : (
                              <span className="font-medium">
                                ${request.final_price_usd}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={request.payment_method === 'wallet' ? "outline" : "secondary"}>
                              {request.payment_method === 'wallet' ? (
                                <div className="flex items-center gap-1">
                                  <Wallet className="h-3 w-3" />
                                  <span>NFT</span>
                                </div>
                              ) : request.payment_method === 'pix' ? (
                                'PIX'
                              ) : (
                                'Cartão'
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/commissions/requests/${request.id}`)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12 border rounded-md bg-muted/20">
                <h3 className="text-lg font-medium mb-2">Nenhuma solicitação recebida</h3>
                <p className="text-muted-foreground mb-4">
                  Você ainda não recebeu nenhuma solicitação de comissão ou todas foram filtradas.
                </p>
                <Button 
                  onClick={() => navigate('/commissions/create')}
                  variant="outline"
                >
                  Criar Oferta de Comissão
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
